<template>
  <div class="wa-connect-page">
    <div class="wa-connect-container">

      <!-- Invalid / expired link -->
      <div v-if="pageState === 'error'" class="state-container">
        <div class="wa-icon wa-icon--error">!</div>
        <h2 class="mt-3">Link not valid</h2>
        <p class="text-muted">{{ errorMessage }}</p>
      </div>

      <!-- Success: WABA connected -->
      <div v-else-if="pageState === 'success'" class="state-container">
        <div class="wa-icon wa-icon--success">&#x2713;</div>
        <h2 class="mt-3">You're connected!</h2>
        <p class="text-muted">Your WhatsApp Business account is now linked. You can close this window.</p>
      </div>

      <!-- Ready to connect -->
      <div v-else class="state-container">
        <div class="wa-icon wa-icon--brand">&#x1F4AC;</div>
        <h2 class="mt-3">Connect WhatsApp</h2>
        <p class="text-muted intro">
          Connect your WhatsApp Business account so your practice can send appointment
          reminders and messages over WhatsApp.
        </p>
        <p class="keep-open" data-test="wa-connect-keep-open">
          Keep this page open while you do it &mdash; you will need it in the Facebook window.
        </p>

        <WhatsAppSignInSteps :phone="linkDetails.phone" :display-name="linkDetails.displayName" />

        <div v-if="errorMessage" class="error-banner">{{ errorMessage }}</div>

        <b-button
          variant="success"
          size="lg"
          class="connect-button"
          :disabled="!sdkReady || connecting"
          @click="connect"
        >
          <span v-if="connecting">
            <b-spinner small class="mr-2" />
            Working...
          </span>
          <span v-else-if="!sdkReady">
            <b-spinner small class="mr-2" />
            Loading...
          </span>
          <span v-else>Connect WhatsApp</span>
        </b-button>
      </div>

      <PoweredByFooter />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import PoweredByFooter from '@/components/PoweredByFooter.vue';
import WhatsAppSignInSteps from './WhatsAppSignInSteps.vue';
import { parseToken, readLinkDetails, buildFbLoginConfig, extractSignupResult } from './embeddedSignup';
import { loadMetaSdk } from '@/utils/metaSdk';

export default {
  name: 'WhatsAppConnect',
  components: { PoweredByFooter, WhatsAppSignInSteps },

  data() {
    return {
      // pageState: 'connect' (default) | 'success' | 'error'
      pageState: 'connect',
      token: '',
      // The number to type into Facebook and the WhatsApp name, read from the link (display only —
      // see readLinkDetails). This page is usually opened by whoever holds the practice's Facebook
      // login, often not the person who set things up, so it has to name the number itself.
      linkDetails: { phone: '', displayName: '' },
      sdkReady: false,
      connecting: false,
      errorMessage: '',
    };
  },

  mounted() {
    this.token = parseToken(window.location.search);
    this.linkDetails = readLinkDetails(this.token);
    if (!this.token) {
      this.pageState = 'error';
      this.errorMessage = 'This link is invalid or has expired. Please contact your practice for a new link.';
      return;
    }

    // Listen for Meta's signup-completion postMessage.
    window.addEventListener('message', this.handleMetaMessage);

    loadMetaSdk()
      .then(() => { this.sdkReady = true; })
      .catch(() => { this.errorMessage = 'We could not load WhatsApp connect. Please refresh the page and try again.'; });
  },

  beforeDestroy() {
    window.removeEventListener('message', this.handleMetaMessage);
  },

  methods: {
    connect() {
      if (!this.sdkReady || this.connecting || !window.FB) return;
      this.errorMessage = '';
      this.connecting = true;

      // VERIFY post-Tech-Provider-approval: FB.login() Embedded Signup config is
      // assumed from Meta's docs (see buildFbLoginConfig). The real waba_id is
      // delivered via the postMessage handler below, not this callback — the
      // callback here only carries the auth `code` (also unverified).
      window.FB.login((response) => {
        // If the user closed the popup without finishing, re-enable the button.
        if (!response || response.status !== 'connected') {
          this.connecting = false;
        }
      }, buildFbLoginConfig({
        configId: process.env.VUE_APP_META_CONFIG_ID,
        solutionId: process.env.VUE_APP_TWILIO_SOLUTION_ID,
      }));
    },

    handleMetaMessage(event) {
      // VERIFY post-Tech-Provider-approval: Meta is documented to post a
      // { type:'WA_EMBEDDED_SIGNUP', event:'FINISH', data:{ waba_id, ... } }
      // message from connect.facebook.net on completion. The origin filter and
      // the JSON-string-vs-object handling below are best-effort until we can
      // observe a live run. extractSignupResult() is defensive and never throws.
      try {
        let data = event && event.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (_) {
            return; // not our JSON message
          }
        }

        // Twilio needs BOTH the WABA id and the Meta phone number id for the Senders
        // API (ticket 28334152 Q2) — it does the token exchange, we never see a token.
        const result = extractSignupResult(data);
        if (!result) return;

        this.completeSignup(result);
      } catch (err) {
        console.error('Error handling Meta signup message:', err);
        this.connecting = false;
        this.errorMessage = 'Something went wrong finishing the connection. Please try again.';
      }
    },

    async completeSignup({ wabaId, phoneNumberId }) {
      try {
        await axios.post(
          '/wa-onboarding/signup-callback',
          { token: this.token, wabaId, phoneNumberId },
          { baseURL: process.env.VUE_APP_TEAMIO_API_URL }
        );
        this.connecting = false;
        this.pageState = 'success';
      } catch (err) {
        console.error('signup-callback failed:', err);
        this.connecting = false;
        this.errorMessage = 'We connected your account but could not save it. Please contact your practice.';
      }
    },
  },
};
</script>

<style scoped>
.wa-connect-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f4f6f8;
  padding: 16px;
}

.wa-connect-container {
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
  padding: 32px 24px 16px;
}

.state-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.wa-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  line-height: 1;
}

.wa-icon--brand {
  background: #e7f6ee;
}

.wa-icon--success {
  background: #25d366;
  color: #fff;
  font-weight: 700;
}

.wa-icon--error {
  background: #fdecea;
  color: #c0392b;
  font-weight: 700;
}

.intro {
  margin-top: 8px;
  margin-bottom: 8px;
}

.keep-open {
  margin-bottom: 16px;
  font-weight: 600;
  font-size: 14px;
}

.connect-button {
  width: 100%;
  margin-top: 8px;
}

.error-banner {
  width: 100%;
  background: #fdecea;
  color: #c0392b;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 14px;
}
</style>
